import {
  Box,
  HStack,
  Input,
  VStack,
  Text,
  Image,
  Button,
} from '@chakra-ui/react';

import { displayNotification } from '../services';
import {
  calculateChunkSize,
  calculateFileSize,
  matchIcon,
  uuid,
} from '../utils';
import { useUploadFiles } from '../hooks';
import { LARGE_FILE_SIZE } from '../constants';

import UploadIcon from '../assets/icons/upload.svg';
import { CustomProgress } from './CustomProgress';
import { FileStatus } from '../models';

export const FilesUploader = ({ attachedId }: { attachedId: number }) => {
  const {
    files,
    setFiles,
    currentFileIndex,
    currentChunkIndex,
    onChangeFileStatus,
    filesStatus,
  } = useUploadFiles(attachedId);

  return (
    <Box w="full">
      <Input
        display="none"
        id="files"
        onChange={e => {
          const files = (e.target as HTMLInputElement).files;
          if (!files) return;

          const transformedFiles = Array.from(files);

          const validFiles = transformedFiles.every(({ size, name }) => {
            if (size > LARGE_FILE_SIZE) {
              displayNotification({
                title: `Invalid File!`,
                description: `The file size of ${name} should be less than 5GB`,
              });

              return false;
            }
            return true;
          });

          if (validFiles)
            setFiles(
              transformedFiles.map(file => ({
                file,
              }))
            );
        }}
        type="file"
        multiple
      />

      <label htmlFor="files">
        <VStack
          cursor="pointer"
          border="2px"
          borderRadius="md"
          borderStyle="dashed"
          borderColor="custom_orange.2"
          py="3"
        >
          <Box border="1px" borderColor="custom_gray.4" borderRadius="md" p="2">
            <Image src={UploadIcon} />
          </Box>

          <Text color="custom_blue.1" fontSize="sm">
            <Box as="span" color="custom_orange.2" fontWeight="semibold">
              Click to upload
            </Box>{' '}
            or drag and drop
          </Text>
          <Text color="custom_blue.1" fontSize="xs">
            Doc, PNG, JPG or GIF Files
          </Text>
        </VStack>
      </label>

      {
        // [
        //   { file: { name: 'text.pdf', size: 15000 } },
        //   { file: { name: 'text.tex', size: 12000 } },
        // ]
        files.map(({ file: { name, size } }, fileIndex) => {
          let progress = 0;
          if (filesStatus[fileIndex] === FileStatus.Completed) {
            progress = 100;
          } else {
            const uploading = fileIndex === currentFileIndex;
            const chunks = Math.ceil(size / calculateChunkSize(size));
            if (uploading && currentChunkIndex) {
              progress = Math.round((currentChunkIndex / chunks) * 100);
            } else {
              progress = 0;
            }
          }

          return (
            <Box
              key={uuid()}
              border="1px"
              borderRadius="md"
              borderColor="custom_gray.4"
              p="3"
              mt="3"
              w="full"
            >
              <HStack justify="space-between" w="full">
                <HStack gap="4" w="full">
                  <Image
                    // TODO: get the default file image from UI/UX
                    fallbackSrc="/icons/txt.svg"
                    src={matchIcon(name)}
                    alt="file icon"
                    boxSize="8"
                  />

                  <VStack align="stretch" w="full">
                    <Box
                      fontWeight="medium"
                      color="custom_blue.1"
                      fontSize="sm"
                    >
                      {name}
                    </Box>

                    <Box color="custom_blue.1" fontSize="sm">
                      {calculateFileSize(size)} MB
                    </Box>

                    <HStack gap="4">
                      <Box flexBasis="80%">
                        <CustomProgress value={progress} isFileUploading />
                      </Box>

                      <Box flexBasis="20%">
                        {filesStatus[fileIndex] !== FileStatus.Completed && (
                          <HStack justify="end">
                            {[
                              FileStatus.InProgress,
                              FileStatus.Resumed,
                            ].includes(filesStatus[fileIndex]) && (
                              <Button
                                onClick={() =>
                                  onChangeFileStatus(
                                    fileIndex,
                                    FileStatus.Paused
                                  )
                                }
                              >
                                Pause
                              </Button>
                            )}

                            {filesStatus[fileIndex] === FileStatus.Paused && (
                              <Button
                                onClick={() =>
                                  onChangeFileStatus(
                                    fileIndex,
                                    FileStatus.Resumed
                                  )
                                }
                              >
                                Resume
                              </Button>
                            )}

                            <Button
                              bg="custom_orange.2"
                              _hover={{ bg: 'custom_orange.2' }}
                              color="white"
                              onClick={() =>
                                onChangeFileStatus(
                                  fileIndex,
                                  FileStatus.Cancelled
                                )
                              }
                            >
                              Cancel
                            </Button>
                          </HStack>
                        )}
                      </Box>
                    </HStack>
                  </VStack>
                </HStack>

                {/* TODO: reuse after implementation */}
                {/* <IconButton
                  icon={<TrashIcon />}
                  aria-label="delete uploaded document file"
                  bg="transparent"
                  onClick={() => {
                    setFiles(files => files.filter(file => file.id !== id));
                  }}
                /> */}
              </HStack>
            </Box>
          );
        })
      }
    </Box>
  );
};
